import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Collapse, Grid, Theme, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { Conversation } from '@twilio/conversations/lib/conversation';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FileAttachmentIcon from '../../../icons/FileAttachmentIcon';
import { useAppState } from '../../../state';
import { isMobile } from '../../../utils';
import SendMessageIcon from '../../../icons/SendMessageIcon';
import Snackbar from '../../Snackbar/Snackbar';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';

const useStyles = makeStyles(theme => ({
  chatInputContainer: {
    borderTop: '1px solid #e4e7e9',
    borderBottom: '1px solid #e4e7e9',
    padding: '1em 1.2em 1em',
  },
  textArea: {
    width: '100%',
    border: '0',
    resize: 'none',
    fontSize: '14px',
    fontFamily: 'Inter',
    outline: 'none',
  },
  button: {
    padding: '0.56em',
    minWidth: 'auto',
    '&:disabled': {
      background: 'none',
      '& path': {
        fill: '#d8d8d8',
      },
    },
  },
  buttonContainer: {
    margin: '1em 0 0 1em',
    display: 'flex',
  },
  fileButtonContainer: {
    position: 'relative',
    marginRight: '1em',
  },
  fileButtonLoadingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  textAreaContainer: {
    display: 'flex',
    marginTop: '0.4em',
    padding: '0.48em 0.7em',
    border: '2px solid transparent',
  },
  quickRepliesContainer: {
    marginBottom: '0.9rem',
  },
  quickRepliesToggle: {
    width: '100%',
    justifyContent: 'space-between',
    padding: '0.35rem 0',
    textTransform: 'none',
    color: 'rgb(40, 42, 43)',
  },
  quickRepliesTitle: {
    color: 'rgb(40, 42, 43)',
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  quickRepliesToggleIcon: {
    color: theme.palette.primary.main,
    transition: 'transform 160ms ease',
  },
  quickRepliesToggleIconClosed: {
    transform: 'rotate(180deg)',
  },
  quickRepliesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    paddingTop: '0.35rem',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '32vh',
      overflowY: 'auto',
      paddingRight: '0.2rem',
    },
  },
  quickReplyButton: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    borderColor: '#d5dadd',
    color: 'rgb(40, 42, 43)',
    padding: '0.55rem 0.8rem',
    lineHeight: 1.35,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      background: 'rgba(1, 145, 88, 0.06)',
    },
  },
  isTextareaFocused: {
    borderColor: theme.palette.primary.main,
    borderRadius: '4px',
  },
}));

interface ChatInputProps {
  conversation: Conversation;
  isChatWindowOpen: boolean;
}

const ALLOWED_FILE_TYPES =
  'audio/*, image/*, text/*, video/*, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document .xslx, .ppt, .pdf, .key, .svg, .csv';

const QUICK_REPLY_MESSAGES = [
  'Um momento, por favor...',
  'Um momento, estou concluindo o atendimento anterior.',
  'Para iniciarmos, por favor, ative seu microfone e câmera.',
  'Para conseguirmos continuar, por favor, saia da sala, habilite seu áudio e vídeo e entre novamente.',
  'Por gentileza, informe seu número de celular, pois irei entrar em contato agora.',
];

export default function ChatInput({ conversation, isChatWindowOpen }: ChatInputProps) {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { participantRole } = useAppState();
  const [messageBody, setMessageBody] = useState('');
  const [isSendingFile, setIsSendingFile] = useState(false);
  const [fileSendError, setFileSendError] = useState<string | null>(null);
  const [isQuickRepliesOpen, setIsQuickRepliesOpen] = useState(!isSmallScreen);
  const isValidMessage = /\S/.test(messageBody);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const isDoctor = participantRole === 'doctor' || new URLSearchParams(window.location.search).get('role') === 'doctor';

  useEffect(() => {
    if (isChatWindowOpen) {
      // When the chat window is opened, we will focus on the text input.
      // This is so the user doesn't have to click on it to begin typing a message.
      textInputRef.current?.focus();
    }
  }, [isChatWindowOpen]);

  useEffect(() => {
    setIsQuickRepliesOpen(!isSmallScreen);
  }, [isSmallScreen]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageBody(event.target.value);
  };

  // ensures pressing enter + shift creates a new line, so that enter on its own only sends the message:
  const handleReturnKeyPress = (event: React.KeyboardEvent) => {
    if (!isMobile && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(messageBody);
    }
  };

  const handleSendMessage = (message: string, clearDraft = true) => {
    const trimmedMessage = message.trim();
    if (/\S/.test(trimmedMessage)) {
      conversation.sendMessage(trimmedMessage);
      if (clearDraft) {
        setMessageBody('');
      }
      textInputRef.current?.focus();
    }
  };

  const handleQuickReplyClick = (message: string) => {
    handleSendMessage(message, false);

    if (isSmallScreen) {
      setIsQuickRepliesOpen(false);
    }
  };

  const handleSendFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      var formData = new FormData();
      formData.append('userfile', file);
      setIsSendingFile(true);
      setFileSendError(null);
      conversation
        .sendMessage(formData)
        .catch(e => {
          if (e.code === 413) {
            setFileSendError('File size is too large. Maximum file size is 150MB.');
          } else {
            setFileSendError('There was a problem uploading the file. Please try again.');
          }
          console.log('Problem sending file: ', e);
        })
        .finally(() => {
          setIsSendingFile(false);
        });
    }
  };

  return (
    <div className={classes.chatInputContainer}>
      <Snackbar
        open={Boolean(fileSendError)}
        headline="Error"
        message={fileSendError || ''}
        variant="error"
        handleClose={() => setFileSendError(null)}
      />
      {isDoctor && (
        <div className={classes.quickRepliesContainer}>
          <Button
            className={classes.quickRepliesToggle}
            onClick={() => setIsQuickRepliesOpen(isOpen => !isOpen)}
            aria-expanded={isQuickRepliesOpen}
            aria-controls="quick-replies-list"
            data-cy-quick-replies-toggle
            endIcon={
              <ExpandMoreIcon
                className={clsx(classes.quickRepliesToggleIcon, {
                  [classes.quickRepliesToggleIconClosed]: !isQuickRepliesOpen,
                })}
              />
            }
          >
            <Typography className={classes.quickRepliesTitle} component="span">
              Respostas rápidas do médico
            </Typography>
          </Button>
          <Collapse in={isQuickRepliesOpen} timeout="auto" unmountOnExit>
            <div id="quick-replies-list" className={classes.quickRepliesList} data-cy-quick-replies-list>
              {QUICK_REPLY_MESSAGES.map(message => (
                <Button
                  key={message}
                  variant="outlined"
                  className={classes.quickReplyButton}
                  onClick={() => handleQuickReplyClick(message)}
                >
                  {message}
                </Button>
              ))}
            </div>
          </Collapse>
        </div>
      )}
      <div className={clsx(classes.textAreaContainer, { [classes.isTextareaFocused]: isTextareaFocused })}>
        {/*
        Here we add the "isTextareaFocused" class when the user is focused on the TextareaAutosize component.
        This helps to ensure a consistent appearance across all browsers. Adding padding to the TextareaAutosize
        component does not work well in Firefox. See: https://github.com/twilio/twilio-video-app-react/issues/498
        */}
        <TextareaAutosize
          minRows={1}
          maxRows={3}
          className={classes.textArea}
          aria-label="chat input"
          placeholder="Write a message..."
          onKeyPress={handleReturnKeyPress}
          onChange={handleChange}
          value={messageBody}
          data-cy-chat-input
          ref={textInputRef}
          onFocus={() => setIsTextareaFocused(true)}
          onBlur={() => setIsTextareaFocused(false)}
        />
      </div>

      <Grid container alignItems="flex-end" justifyContent="flex-end" wrap="nowrap">
        {/* Since the file input element is invisible, we can hardcode an empty string as its value.
        This allows users to upload the same file multiple times. */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleSendFile}
          value={''}
          accept={ALLOWED_FILE_TYPES}
        />
        <div className={classes.buttonContainer}>
          <div className={classes.fileButtonContainer}>
            <Button className={classes.button} onClick={() => fileInputRef.current?.click()} disabled={isSendingFile}>
              <FileAttachmentIcon />
            </Button>

            {isSendingFile && <CircularProgress size={24} className={classes.fileButtonLoadingSpinner} />}
          </div>

          <Button
            className={classes.button}
            onClick={() => handleSendMessage(messageBody)}
            color="primary"
            variant="contained"
            disabled={!isValidMessage}
            data-cy-send-message-button
          >
            <SendMessageIcon />
          </Button>
        </div>
      </Grid>
    </div>
  );
}
